```vue
<template>
  <div class="fixed bottom-4 right-4 z-40">
    <!-- Calculator Button -->
    <button
      @click="isOpen = !isOpen"
      class="bg-indigo-500 text-white p-3 rounded-full shadow-lg hover:bg-indigo-600 transition-colors"
    >
      <span v-if="selectedProducts.length > 0" class="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
        {{ selectedProducts.length }}
      </span>
      <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
    </button>

    <!-- Calculator Panel -->
    <div
      v-if="isOpen"
      class="absolute bottom-16 right-0 w-96 bg-white rounded-lg shadow-xl border border-slate-200"
    >
      <div class="p-4 border-b border-slate-200">
        <div class="flex justify-between items-center">
          <h3 class="text-lg font-semibold text-slate-800">Calculadora</h3>
          <button @click="isOpen = false" class="text-slate-400 hover:text-slate-600">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div class="p-4">
        <!-- Selected Products List -->
        <div v-if="selectedProducts.length > 0" class="space-y-3 mb-4">
          <div
            v-for="product in selectedProducts"
            :key="product.id"
            class="flex items-center justify-between bg-slate-50 p-2 rounded"
          >
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-800">{{ product.name }}</p>
              <p class="text-xs text-slate-600">${{ product.unitPrice.toLocaleString() }}</p>
            </div>
            <button
              @click="removeProduct(product.id)"
              class="text-red-500 hover:text-red-700"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-4 text-slate-600">
          <p>Seleccione productos de la lista para calcular el total</p>
        </div>

        <!-- Total -->
        <div class="mt-4 pt-4 border-t border-slate-200">
          <div class="flex justify-between items-center">
            <span class="text-lg font-medium text-slate-700">Total:</span>
            <span class="text-xl font-bold text-indigo-600">
              ${{ total.toLocaleString() }}
            </span>
          </div>
        </div>

        <!-- Clear Button -->
        <button
          v-if="selectedProducts.length > 0"
          @click="clearAll"
          class="w-full mt-4 px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
        >
          Limpiar Todo
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

interface SelectedProduct {
  id: string;
  name: string;
  unitPrice: number;
}

const isOpen = ref(false);
const selectedProducts = ref<SelectedProduct[]>([]);

const total = computed(() => {
  return selectedProducts.value.reduce((sum, product) => sum + product.unitPrice, 0);
});

const addProduct = (product: SelectedProduct) => {
  if (!selectedProducts.value.some(p => p.id === product.id)) {
    selectedProducts.value.push(product);
    isOpen.value = true;
  }
};

const removeProduct = (productId: string) => {
  selectedProducts.value = selectedProducts.value.filter(p => p.id !== productId);
};

const clearAll = () => {
  selectedProducts.value = [];
};

defineExpose({
  addProduct
});
</script>
```